import Swal from 'sweetalert2';
import { Metric } from '../../../../../api/apiTypes';

export const showIndicatoreModal = (metric: Metric) => {
  let legend = [
    { range: "0 - 2", meaning: "Prestazione bassa" },
    { range: "3 - 5", meaning: "Prestazione sufficiente" },
    { range: "6 - 8", meaning: "Buona prestazione" },
    { range: "9 - 10", meaning: "Eccellente prestazione" },
  ];

  let extraParagraph = "";

  switch (metric.title) {
    case "Completezza":
      extraParagraph = `
        <p style="font-size:13px; color:#374151; margin-bottom:10px;">
          Questa metrica valuta la completezza e la veridicità del riassunto generato (summary) rispetto al
          contesto fornito (Retrieval Context). Verifica che il riassunto estragga tutte le informazioni
          necessarie per completare il task descritto nell'input, senza includere dettagli secondari, irrilevanti o
          contraddittori.
        </p>
      `;
      legend = [
        {
          range: "0 - 1",
          meaning: "Il riassunto contiene poche o nessuna delle informazioni rilevanti presenti nel Retrieval Context necessarie per il task. In alternativa, si concentra su informazioni secondarie, non rilevanti, fuorvianti o inventate."
        },
        {
          range: "3 - 4",
          meaning: "Il riassunto contiene alcune delle informazioni rilevanti necessarie, ma include anche informazioni secondarie, non rilevanti o fuorvianti."
        },
        {
          range: "6 - 7",
          meaning: "Il riassunto contiene la maggior parte delle informazioni rilevanti. In alternativa, le contiene tutte ma include anche informazioni secondarie, non rilevanti o fuorvianti."
        },
        {
          range: "9 - 10",
          meaning: "Il riassunto contiene tutte le informazioni rilevanti necessarie per il task e nessuna informazione secondaria, non rilevante o fuorviante. I punteggi ottenuti sono normalizzati utilizzando le probabilità dei token prodotti dal Large Language Model."
        },
      ];
      break;

    case "Coerenza ragionamento":
      extraParagraph = `
        <p style="font-size:13px; color:#374151; margin-bottom:10px;">
          Questa metrica misura la coerenza logica tra il riassunto (summary), il ragionamento (reasoning) e
          il task descritto nell'input. Controlla che il ragionamento derivi logicamente dal riassunto, che non
          contenga "allucinazioni" (informazioni non supportate), che le frasi rilevanti (relevant sentences)
          supportino le affermazioni e che le istruzioni del task siano state seguite correttamente.
        </p>
      `;
      legend = [
        {
          range: "0 - 4",
          meaning: "Il ragionamento manca di adeguata coerenza con il riassunto e/o il task. Ciò include: affermazioni non supportate (allucinazioni), struttura illogica o incoerente, fraintendimento del contenuto del riassunto, violazione delle istruzioni del task, o affermazioni vaghe e contraddittorie. Un riferimento minimo al riassunto non è sufficiente se il ragionamento generale non è supportato."
        },
        {
          range: "6 - 10",
          meaning: "Il ragionamento è pienamente o quasi del tutto coerente con il riassunto e allineato alle istruzioni. Ciò include: affermazioni chiave logicamente consistenti e riconducibili al riassunto, flusso logico chiaro, pieno rispetto delle istruzioni del task e inferenze ragionevoli e ben motivate. Sono accettabili omissioni minori se il ragionamento centrale rimane fedele e giustificabile."
        },
      ];
      break;

    case "Rispetto delle eccezioni":
      extraParagraph = `
        <p style="font-size:13px; color:#374151; margin-bottom:10px;">
          Questa metrica, molto specifica, verifica la conformità del ragionamento (reasoning) a istruzioni
          speciali fornite all'interno dei tag &lt;Notes&gt;&lt;/Notes&gt; presenti nell'input.
        </p>
      `;
      legend = [
        { range: "0", meaning: "Il ragionamento contraddice una o più istruzioni speciali." },
        { range: "10", meaning: "Il ragionamento non contraddice nessuna istruzione speciale. I punteggi ottenuti sono normalizzati utilizzando le probabilità dei token prodotti dal Large Language Model." },
      ];
      break;

    case "Conoscenza di dominio":
      extraParagraph = `
        <p style="font-size:13px; color:#374151; margin-bottom:10px;">
          Questa metrica valuta se il ragionamento dimostra una corretta comprensione delle definizioni
          tecniche fornite nell'input. La sua implementazione e i suoi descrittori cambiano in base al parametro task_type.
        </p>
      `;
      if (metric.task_type === "be_strategy") {
        legend = [
          { range: "0 - 1", meaning: "Il ragionamento mostra che il modello non ha alcuna comprensione delle definizioni e delle differenze tra i tipi di strategie." },
          { range: "3 - 4", meaning: "Le prove fornite nel ragionamento per supportare la conclusione sono parzialmente incoerenti con la definizione della strategia selezionata." },
          { range: "6 - 7", meaning: "Le prove sono sufficientemente coerenti con la definizione della strategia selezionata. Tuttavia, il ragionamento non include considerazioni sul confronto tra i diversi tipi di strategie." },
          { range: "9 - 10", meaning: "Le prove sono pienamente coerenti con la definizione della strategia selezionata e includono anche un confronto tra le strategie ('static' e 'dynamic'), dimostrando una comprensione profonda di entrambe." },
        ];
      } else if (metric.task_type === "advanced_features") {
        legend = [
          { range: "0 - 4", meaning: "Il ragionamento mostra che il modello non ha alcuna comprensione delle definizioni delle funzionalità avanzate." },
          { range: "7 - 10", meaning: "Le prove fornite nel ragionamento per supportare la conclusione sono coerenti con le definizioni delle funzionalità avanzate. Punteggi ottenuti sono normalizzati utilizzando le probabilità dei token prodotti dal Large Language Model." },
        ];
      }
      break;
  }

  Swal.fire({
    title: `<h2 style="font-size:20px; font-weight:600; color:#111827;">${metric.title}</h2>`,
    html: `
      <div style="text-align:left; margin-top:10px;">
        <p style="font-size:14px; color:#374151; margin-bottom:10px;">
          <strong>Punteggio:</strong> ${metric.value ?? "N/A"}
        </p>
        ${extraParagraph}
        <h4 style="font-size:16px; font-weight:500; color:#111827; margin-top:15px;">Legenda</h4>
        <ul style="list-style:none; padding:0; margin-top:6px;">
          ${legend
            .map(
              l =>
                `<li style="font-size:13px; color:#374151; margin-bottom:4px;">
                  <strong>${l.range}</strong> → ${l.meaning}
                </li>`
            )
            .join("")}
        </ul>
      </div>
    `,
    confirmButtonText: "Chiudi",
    confirmButtonColor: "#374151",
    width: 600,
    background: "#fff",
  });
};
